// utils/tfidf.ts

const stripHtml = (html: string): string => {
  if (!html) return '';
  // A simple regex approach for environments without DOMParser or for simplicity
  return html.replace(/<[^>]*>?/gm, ' ');
};

const tokenize = (text: string): string[] => {
  if (!text) return [];
  // Basic tokenization: lowercase, remove non-alphanumeric (keeping Polish chars), split by space
  return text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, '') // Keep letters and numbers from all languages
    .replace(/\s+/g, ' ')
    .trim()
    .split(' ');
};

const preprocess = (html: string): string[] => {
  return tokenize(stripHtml(html));
};

const getTf = (tokens: string[]): Map<string, number> => {
  const tf = new Map<string, number>();
  const tokenCount = tokens.length;
  if (tokenCount === 0) return tf;
  for (const token of tokens) {
    tf.set(token, (tf.get(token) || 0) + 1);
  }
  for (const [token, count] of tf.entries()) {
    tf.set(token, count / tokenCount);
  }
  return tf;
};

export class TfidfProcessor {
  private categoryDocs: { id: number; tokens: string[] }[] = [];
  private vocabulary: Set<string> = new Set();
  private idfMap: Map<string, number> = new Map();
  private categoryVectors: { id: number; vector: Map<string, number> }[] = [];

  constructor(categories: { id: number; name: string; description?: string; short_description?: string }[]) {
    this.initialize(categories);
  }

  private initialize(categories: { id: number; name: string; description?: string; short_description?: string }[]) {
    // 1. Preprocess and store docs
    this.categoryDocs = categories.map(cat => ({
      id: cat.id,
      tokens: preprocess(`${cat.name} ${cat.description || ''} ${cat.short_description || ''}`)
    }));
    const allDocsTokens = this.categoryDocs.map(d => d.tokens);
    const numDocs = allDocsTokens.length;

    if (numDocs === 0) return;

    // 2. Build vocabulary
    for (const tokens of allDocsTokens) {
      for (const token of tokens) {
        this.vocabulary.add(token);
      }
    }
    
    // 3. Build IDF map
    for (const term of this.vocabulary) {
      const docsWithTerm = allDocsTokens.filter(tokens => tokens.includes(term)).length;
      const idf = Math.log(numDocs / (1 + docsWithTerm));
      this.idfMap.set(term, idf);
    }
    
    // 4. Pre-calculate category vectors
    this.categoryVectors = this.categoryDocs.map(doc => {
        const tf = getTf(doc.tokens);
        const vector = new Map<string, number>();
        for (const term of this.vocabulary) {
            const termFrequency = tf.get(term) || 0;
            if (termFrequency > 0) {
                const idf = this.idfMap.get(term) || 0;
                vector.set(term, termFrequency * idf);
            }
        }
        return { id: doc.id, vector };
    });
  }

  private getArticleVector(articleHtml: string): Map<string, number> {
    const tokens = preprocess(articleHtml);
    const tf = getTf(tokens);
    const vector = new Map<string, number>();

    for (const term of this.vocabulary) {
        const termFrequency = tf.get(term) || 0;
        if (termFrequency > 0) {
            const idf = this.idfMap.get(term) || 0;
            vector.set(term, termFrequency * idf);
        }
    }
    return vector;
  }

  private cosineSimilarity(vecA: Map<string, number>, vecB: Map<string, number>): number {
    let dotProduct = 0;
    let magA = 0;
    let magB = 0;
    
    const allTerms = new Set([...vecA.keys(), ...vecB.keys()]);

    for (const term of allTerms) {
      const valA = vecA.get(term) || 0;
      const valB = vecB.get(term) || 0;
      dotProduct += valA * valB;
      magA += valA * valA;
      magB += valB * valB;
    }

    magA = Math.sqrt(magA);
    magB = Math.sqrt(magB);
    
    if (magA === 0 || magB === 0) return 0;
    
    return dotProduct / (magA * magB);
  }

  public getSuggestions(articleHtml: string, topN: number = 6): { id: number; score: number }[] {
    const articleVector = this.getArticleVector(articleHtml);
    
    const suggestions = this.categoryVectors.map(cat => ({
      id: cat.id,
      score: this.cosineSimilarity(articleVector, cat.vector)
    }));

    return suggestions
      .filter(s => s.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, topN);
  }
}
